import { useEffect } from "react";
import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import { DiscountFormData } from "../../types/discount";
import { FancyTextInput } from "../reuseables/form.comp";
import { selectCss } from "../reuseables/native.comp";
import { EditorSection, Title } from "../reuseables/editor.comp";

export default function ScheduleDiscountBuyXForm({ methods }: { methods: UseFormReturn<DiscountFormData, any> }) {
  const { t, msg } = useTranslation();
  const kind = useWatch({ control: methods.control, name: "scheduleDiscountKind" }) ?? "simple";

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "buyXGetYPercentOff.tiers",
  });

  useEffect(() => {
    if (kind !== "buyXGetY%Off") return;
    const tiers = methods.getValues("buyXGetYPercentOff.tiers");
    if (!tiers?.length) {
      methods.setValue("buyXGetYPercentOff.tiers", [{ minBuyQty: 2, discountPercentage: 10 }]);
    }
  }, [kind, methods]);

  return (
    <EditorSection>
      <Title>{t(msg.discount.scheduleDiscountMode)}</Title>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-slate-700 dark:text-slate-200 mb-1">{t(msg.discount.scheduleDiscountMode)}</label>
          <select
            id="schedule-discount-kind-select"
            data-schedule-discount-kind
            className={selectCss}
            {...methods.register("scheduleDiscountKind")}
          >
            <option value="simple">{t(msg.discount.scheduleDiscountKindSimple)}</option>
            <option value="buyXGetY%Off">{t(msg.discount.scheduleDiscountKindBuyX)}</option>
          </select>
        </div>

        {kind === "buyXGetY%Off" ? (
          <div id="schedule-discount-buyx-section" data-schedule-buyx-section className="space-y-4">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {t(msg.discount.byProducts)} / {t(msg.discount.byCategories)} — {t(msg.discount.scheduleDiscountKindBuyX)}
            </div>

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="flex flex-wrap gap-3 items-end border-t border-slate-200 dark:border-slate-600 pt-3"
                data-schedule-buyx-tier-row={index}
              >
                <label className="block">
                  <span className="block text-xs text-slate-600 dark:text-slate-300 mb-1">{t(msg.discount.buyXMinBuyQty)}</span>
                  <FancyTextInput
                    id={
                      index === 0
                        ? "schedule-discount-buyx-tier-0-min"
                        : index === 1
                          ? "schedule-discount-buyx-tier-1-min"
                          : undefined
                    }
                    type="number"
                    register={() =>
                      methods.register(`buyXGetYPercentOff.tiers.${index}.minBuyQty`, {
                        valueAsNumber: true,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-slate-600 dark:text-slate-300 mb-1">{t(msg.discount.discountPercentage)}</span>
                  <FancyTextInput
                    id={
                      index === 0
                        ? "schedule-discount-buyx-tier-0-pct"
                        : index === 1
                          ? "schedule-discount-buyx-tier-1-pct"
                          : undefined
                    }
                    type="number"
                    register={() =>
                      methods.register(`buyXGetYPercentOff.tiers.${index}.discountPercentage`, {
                        valueAsNumber: true,
                      })
                    }
                  />
                </label>
                {fields.length > 1 ? (
                  <button
                    type="button"
                    data-schedule-buyx-remove-tier={index}
                    className="text-sm text-red-600 dark:text-red-400 mb-1"
                    onClick={() => remove(index)}
                  >
                    {t(msg.discount.buyXRemoveTier)}
                  </button>
                ) : null}
              </div>
            ))}

            <button
              type="button"
              id="schedule-discount-buyx-add-tier"
              data-schedule-buyx-add-tier
              className="text-white bg-her-dodgerblue dark:bg-blue-600 px-3 py-1 rounded text-sm"
              onClick={() => append({ minBuyQty: 1, discountPercentage: 10 })}
            >
              {t(msg.discount.buyXAddTier)}
            </button>

            <label className="block max-w-xs">
              <span className="block text-xs text-slate-600 dark:text-slate-300 mb-1">{t(msg.discount.buyXMaxDiscountedUnits)}</span>
              <FancyTextInput
                id="schedule-discount-buyx-max-units"
                type="number"
                register={() =>
                  methods.register("buyXGetYPercentOff.maxDiscountedUnits", {
                    valueAsNumber: true,
                  })
                }
              />
            </label>
          </div>
        ) : null}
      </div>
    </EditorSection>
  );
}
